import { Response as GetClient } from 'api/typegen/client/get';
import { Response as GetClientApplication } from 'api/typegen/client/application/get';
import { ROUTES } from 'utils/constants/constants_routes';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useClientBlockedStatus from './useClientBlockedStatus';

// https://wiki.4finance.net/display/FD/Resolution+Mapping+-+Vivus.es

export const useMaybeNavigateByResolution = () => {
  const navigate = useNavigate();
  const { clientBlockedStatus, fetchClientBlockedStatus } =
    useClientBlockedStatus();

  // Either returns navigate function or nothing
  const maybeNavigateByResolution = useCallback(
    async ({
      application,
      client,
    }: {
      application: GetClientApplication | null | undefined;
      client: GetClient | undefined | null;
    }) => {
      const { resolution, resolutionDetail, created } = application || {};

      const clientBlocked =
        clientBlockedStatus || (await fetchClientBlockedStatus());

      if (client?.blocked) {
        navigate(ROUTES.FIRST_LOAN_REJECTED);
        return true;
      }

      if (!resolution) {
        return;
      }

      if (resolution === 'APPROVED') {
        navigate(ROUTES.FIRST_LOAN_ONLINE_BANK_APPROVED);
        return true;
      }

      if (resolution === 'CANCELLED') {
        navigate(ROUTES.FIRST_LOAN_WELCOME);
        return true;
      }

      if (!resolutionDetail || !created) {
        return;
      }

      if (resolution === 'REJECTED') {
        if (clientBlocked?.blocked) {
          navigate(ROUTES.REJECTED_TIMER);
        } else {
          navigate(ROUTES.FIRST_LOAN_WELCOME);
        }
        return true;
      }

      if (resolution === 'MANUAL')
        if (
          [
            'LENGTH_INSTANTOR',
            'FIRST_CHARACTER_ZERO',
            'INSTANTOR_NAME_MISMATCH',
            'INSTANTOR_ID_NULL_RESPONSE',
            'NAME_MATCH_MISSING',
            'RISKY_TELEPHONE',
            'AML',
            'ERROR',
            'PERSONAL_ID_MISSING',
            'EXPIRED_IDENTITY_DOCUMENT',
            'MANUAL_APPROVAL_IN_PROGRESS',
          ].includes(resolutionDetail)
        ) {
          if (
            client?.markedForManualIdentification &&
            !client?.hasAttachments
          ) {
            navigate(ROUTES.FIRST_LOAN_ONLINE_BANK_ADD_DOCUMENTS);
            return true;
          }

          if (client?.bankAccount && client?.hasAttachments) {
            navigate(ROUTES.FIRST_LOAN_ONLINE_BANK_PENDING_WAITING);
            return true;
          }

          if (client?.hasAttachments) {
            navigate(ROUTES.FIRST_LOAN_ONLINE_BANK_IBAN);
            return true;
          }

          navigate(ROUTES.FIRST_LOAN_ONLINE_BANK_ADD_DOCUMENTS);
          return true;
        }

      if (client?.bankAccount && client?.hasAttachments) {
        navigate(ROUTES.FIRST_LOAN_MANUAL_UPLOAD_WAITING);
        return true;
      }

      if (client?.hasAttachments) {
        navigate(ROUTES.FIRST_LOAN_MANUAL_UPLOAD_BANK_ACCOUNT);
        return true;
      }

      navigate(ROUTES.FIRST_LOAN_ONLINE_BANK_ADD_DOCUMENTS);
      return true;
    },
    [clientBlockedStatus, navigate],
  );

  return {
    maybeNavigateByResolution,
  };
};
